import { useMemo } from 'react'
import { COLUMN_MAX, COLUMN_PRIORITY, CONTENT_WIDTH } from '../../theme/tokens'

/** `KERN` — the step that is visible at EVERY container width (Konzept §3.2.2:444). */
export const KERN = 'kern'

/** `FREI` — the step that never becomes visible on its own (Konzept §3.2.2:448). */
export const FREI = 'frei'

/** `MUSTERBREITE` — die Breite, die eine Flaeche fuehrt, BEVOR sie ihre eigene kennt.
 *
 *  Konzept §4.3:623-624: *„eine Flaeche, die ihre Breite nicht kennt, benutzt die
 *  Vorgabe ihres Musters — nie 0, nie `100 %`"*. Genommen ist `CONTENT_WIDTH.reading`
 *  (1280) — der **Deckel** des Musters `reading`, NICHT eine Behaelterbreite (s. die
 *  ausfuehrliche Begruendung im JSDoc unten, R16-W-N1) —, weil dieser Hook das Muster
 *  der SEITE nicht kennt: der `PageWidthContext` entsteht erst in G0b.2, sein erster
 *  Verbraucher (`TableViewport`, G0b.4) uebergibt
 *  `width ?? CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading`; fuer `full` traegt das
 *  Muster `null` (§1.1:156), dann greift `CONTENT_WIDTH.reading` (§4.3:614-616,
 *  R16-W-N2). ⚠️ *Abgeleitet, nicht Owner-Wortlaut.* */
const MUSTERBREITE = CONTENT_WIDTH.reading

const hat = (objekt, schluessel) => Object.prototype.hasOwnProperty.call(objekt, schluessel)

/** Die Breite, die eine Spalte im Budget KOSTET — ihr eigenes `max`, sonst das ihrer Art.
 *
 *  🔴 **Warum es dieses Feld gibt** (G14a-4 R16-C2, Nachbesserung 2). Das Budget war
 *  `Σ COLUMN_MAX[kind].max` — die OBERE Grenze der Art. Eine Ansicht, die eine Spalte
 *  absichtlich FEST setzt (A8: Time 150, Recipient 220, Status 110), rechnete damit
 *  gegen 160/360/200 und liess eine Spalte fallen, die real ~324 px Luft gehabt haette
 *  (Fotobefund: Transport fehlte bei 1440, obwohl das Panel sie trug).
 *
 *  🔴 **Additiv und byte-gleich ohne das Feld:** fehlt `max`, steht dort ZUERST die
 *  Art (`COLUMN_MAX[kind].max`) — jeder bestehende Aufrufer bekommt dieselbe Zahl wie
 *  vorher. `vergleicheOptional` (die Reihenfolge) liest dieselbe Funktion, sonst
 *  sortierte der Fit nach einer anderen Breite als der, mit der er rechnet.
 *
 *  Ein unbrauchbarer Wert (`NaN`, `0`, negativ) faellt auf die Art zurueck, statt das
 *  Budget zu vergiften — `NaN` liesse JEDE Summe `NaN` werden, und `budget >= breite`
 *  waere dann immer falsch (kein optionales Feld je sichtbar). */
const maxBreite = (spalte) => {
  const eigen = spalte.max
  if (Number.isFinite(eigen) && eigen > 0) return eigen
  return COLUMN_MAX[spalte.kind]?.max ?? 0
}

/** Prueft EINEN Descriptor gegen die zwei geschlossenen Mengen des Konzepts.
 *
 *  Beide werden geprueft, obwohl der Hook nur `priority` liest: `kind` ist der
 *  EINGANG der Spaltenbreiten (`COLUMN_MAX[kind]`, §3.3:500-504), und die
 *  Ausgabe dieses Hooks (`visible`) ist genau die Menge, ueber die das Budget
 *  gebildet wird (§4.1:567). Eine unbekannte Art faellt sonst erst dort auf —
 *  als `undefined`, eine Ebene weiter, an der falschen Stelle.
 *
 *  🔴 **Zwei Ausgaenge, je Umgebung einer** (R15-Warnung 1, Nachbesserung 1):
 *  AUSSERHALB von `production` wird geworfen — dort ist ein falscher Deskriptor
 *  ein Programmierfehler und soll laut sein. IN `production` wird gemeldet und
 *  die Spalte als `kern` behandelt: der Wurf liegt im `useMemo`, also WAEHREND
 *  des Renderns, und reisst in React 18 ohne Error Boundary den GANZEN Baum ab.
 *  Ein Tippfehler in EINEM Deskriptor naehme damit die Seite mit — ab G0b.4
 *  haengen 25 `TableViewport`-Verbraucher daran. Daten zu verstecken waere
 *  schlimmer als eine falsch einsortierte Spalte, deshalb bleibt sie sichtbar.
 *
 *  @returns {boolean} `true`, wenn der Deskriptor die Mengen einhaelt. */
function pruefeDescriptor(spalte, i) {
  const ort = `columns[${i}]`
  const melde = (text) => {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error(text)
      return false
    }
    throw new Error(text)
  }
  if (spalte === null || typeof spalte !== 'object') {
    return melde(`useColumnPriority: ${ort} ist kein Descriptor ({ key, priority, kind }).`)
  }
  if (typeof spalte.key !== 'string' || spalte.key === '') {
    return melde(`useColumnPriority: ${ort} traegt keinen \`key\` — die Spalte ist nicht adressierbar.`)
  }
  if (!hat(COLUMN_PRIORITY, spalte.priority)) {
    return melde(
      `useColumnPriority: ${ort} traegt die unbekannte Stufe \`${spalte.priority}\`. ` +
        `Erlaubt sind: ${Object.keys(COLUMN_PRIORITY).join(', ')}.`,
    )
  }
  if (!hat(COLUMN_MAX, spalte.kind)) {
    return melde(
      `useColumnPriority: ${ort} traegt die unbekannte Spaltenart \`${spalte.kind}\`. ` +
        `Erlaubt sind: ${Object.keys(COLUMN_MAX).join(', ')}.`,
    )
  }
  return true
}

/** Macht aus einem beanstandeten Deskriptor eine sichtbare Spalte — nur in `production`.
 *
 *  Die Stufe wird auf `kern` gesetzt, alles andere bleibt stehen. Ein Deskriptor,
 *  der gar kein Objekt ist, hat nichts, was stehenbleiben koennte; er wird zur
 *  nackten `kern`-Spalte (ohne `key` — die Kennung fehlt ohnehin schon). */
const alsKern = (spalte) =>
  spalte !== null && typeof spalte === 'object' ? { ...spalte, priority: KERN } : { priority: KERN }

/** Die Reihenfolge, in der die OPTIONALEN Spalten aufgenommen werden (G1b-Nachbesserung 1).
 *
 *  🔴 **Zuerst die STUFE** — `COLUMN_PRIORITY` ordnet `kern` vor `lg` vor `xl` vor `xxl`
 *  (0 < 1024 < 1360 < 1728). Die Stufe ist damit ein **RANG**, keine Schwelle mehr.
 *  **Dann die KLEINERE Spalte** (`maxBreite` = `max` des Deskriptors, sonst
 *  `COLUMN_MAX[kind].max`): sie kostet weniger Platz, also passen mehr Spalten in dieselbe
 *  Breite — „der Ueberschuss geht in SPALTEN, nicht in breitere Zellen" (§3.3:506-507).
 *  **Zuletzt die Eingabereihenfolge** (stabil).
 *
 *  ⚠️ **Warum nicht die reine Eingabereihenfolge.** Die Spaltenliste steht in
 *  ANZEIGEordnung (`Product · Category · On hand · Stock-out · List · …`), nicht in
 *  Wichtigkeitsordnung. `Category` (max 320) steht dort VOR `Stock-out` (max 160) und
 *  naehme bei ~900 px den Platz weg, den `Stock-out` (die Frist) braucht — die Auswahl
 *  haenge sonst an der zufaelligen Spaltenreihenfolge statt an der Groesse. */
function vergleicheOptional(a, b) {
  const rangA = COLUMN_PRIORITY[a.spalte.priority] ?? Infinity
  const rangB = COLUMN_PRIORITY[b.spalte.priority] ?? Infinity
  if (rangA !== rangB) return rangA - rangB
  const maxA = maxBreite(a.spalte)
  const maxB = maxBreite(b.spalte)
  if (maxA !== maxB) return maxA - maxB
  return a.i - b.i
}

/** Waehlt die sichtbaren Spalten: **`kern` immer, dann auffuellen, bis der Behaelter steht.**
 *
 *  🔴 **Ein GREEDY FIT, keine Schwellenpruefung** (G1b-Nachbesserung 1, Befund 1). `kern`
 *  steht immer (auch im Rollfall, §3.2.2:444). Danach werden die optionalen Spalten in
 *  `vergleicheOptional`-Reihenfolge aufgenommen, SOLANGE das aufgelaufene Budget **unter**
 *  der Behaelterbreite liegt; die Spalte, die den Behaelter ueberschreitet, kommt noch mit
 *  (die Tabelle deckelt danach auf den Behaelter, §4.1:568). Damit fuellt die Tabelle den
 *  Raum, statt bei zwei Spalten zu enden und ~224 px leer zu lassen.
 *
 *  🔴 **Warum nicht die Schwelle.** Die Stufen sind ANFANGSWERTE, nicht gemessen
 *  (§3.2.2:439-440; Plan §2.6:279-280) — und die Messung widerlegt sie an der Zielbreite:
 *  bei 1280 Viewport ist der Behaelter **904**, die `lg`-Schwelle **1024** liegt DARUEBER,
 *  also zeigte die Liste dort nur `kern` (das Konzept nennt den „ehrlichen Nachteil" selbst,
 *  §3.2.2:462-467). Das Konzept gibt den Ausweg vor: *„faellt `kern` + `lg` dort
 *  nachweislich in 904 px, wandert `lg` auf ≤ 904 — die Zahl folgt dann der Messung"*
 *  (§3.2.2:465-467). Eine gemeinsame `lg`-Schwelle leistet das aber nicht: `kern` + ALLE
 *  `lg` (7 Spalten, Budget 1520) passen nicht in 904, `kern` + EINE `lg` (4-5 Spalten) schon.
 *  Der Fit entscheidet deshalb JE SPALTE, nicht je Stufe.
 *
 *  🔴 `frei` wird nie automatisch aufgenommen (§3.2.2:448) — nur ueber die Nutzerauswahl
 *  (OE-W5, §3.5:520-533). Die Mengen bleiben MONOTON (§3.2.2:450-451): waechst der Behaelter,
 *  waechst nur die Zahl der aufgenommenen Spalten, nie faellt eine heraus.
 *
 *  @returns {Array<object>} die sichtbaren Spalten in EINGABEREIHENFOLGE (Identitaet). */
// 🔴 G8b (2026-10-21): als BENANNTER Export herausgegeben — additiv, der Hook
// ruft dieselbe Funktion. Grund: das Ladegeruest und seine Jest-Proben muessen
// die sichtbare Menge fuer eine GEGEBENE Breite vorhersagen koennen, ohne einen
// Hook zu rufen (jsdom hat kein Layout, `useContainerWidth` liefert dort `null`).
// Eine im Test nachgebaute Kopie der Fit-Regel waere eine zweite Quelle.
export function waehleSichtbare(spalten, breite) {
  const gewaehlt = new Set(spalten.filter((spalte) => spalte.priority === KERN))
  let budget = [...gewaehlt].reduce((summe, spalte) => summe + maxBreite(spalte), 0)
  const optional = spalten
    .map((spalte, i) => ({ spalte, i }))
    .filter(({ spalte }) => spalte.priority !== KERN && spalte.priority !== FREI)
    .sort(vergleicheOptional)
  for (const { spalte } of optional) {
    if (budget >= breite) break
    gewaehlt.add(spalte)
    budget += maxBreite(spalte)
  }
  return spalten.filter((spalte) => gewaehlt.has(spalte))
}

/**
 * useColumnPriority — container width + column steps → the columns that fit (BW5, UI2 Teil 10, G0b.3).
 *
 * @description
 *   A table does not hide its columns by the viewport but by the room it actually
 *   has. `kern` is always visible, `frei` never on its own; the optional columns are
 *   filled in **priority order until the container stands** (G1b-Nachbesserung 1 —
 *   see `waehleSichtbare`). The sets are MONOTONE: what is visible at 904 is visible
 *   at 2184, a column never disappears when the window grows (§3.2.2:450-451).
 *
 *   🔴 **The step is a RANK, not a threshold** (G1b-Nachbesserung 1). The concept's
 *   step table gives `kern` < `lg` < `xl` < `xxl` (§3.2.2:445-447) — that ORDER is
 *   what this hook uses. The absolute thresholds (`1024`/`1360`/`1728`) are *„Startwerte,
 *   gesetzt — nicht gemessen"* (§3.2.2:439-440), and the measurement refutes them at
 *   the target width: at a 1280 viewport the container is **904**, below the `lg`
 *   threshold **1024**, so a pure threshold test showed only `kern` and left ~224 px of
 *   the panel empty. §3.2.2:465-467 prescribes the remedy (*„wandert `lg` auf ≤ 904 —
 *   die Zahl folgt dann der Messung"*); the per-column fit is its faithful form, because
 *   one shared `lg` threshold cannot separate „7 `lg` columns do not fit in 904" from
 *   „1-2 of them do".
 *
 *   🔴 **The descriptor carries `kind`; `min`/`max` come from `COLUMN_MAX[kind]`** (Grill W12,
 *   §3.3:500-504, NW3.3:550): a column is `{ key, priority, kind }` — ONE source for the one
 *   number. The concept allows a deviation only with a reason in the descriptor itself (a
 *   comment plus an `[ABWEICHUNG: …]` field) — and the ONE deviation this hook carries is
 *   exactly the optional `max` described in the next paragraph, not a free-form second
 *   width. This hook therefore reads `priority` for the decision and requires `kind` to
 *   exist, so that the derived width has a source at all.
 *
 *   🔴 **`max` is the ONE optional deviation, and it is additive** (G14a-4 R16-C2,
 *   Nachbesserung 2, 2026-11-01). A view that FIXES a column (A8: Time 150, Recipient 220,
 *   Status 110) budgets against the type's upper bound and drops a column that would have
 *   fitted. Such a view may therefore carry `max` on the descriptor; it replaces the number
 *   for the BUDGET (fit and order), never for `min` (that stays the type's). Without the
 *   field every caller is byte-identical — the fallback is the type's `max`.
 *
 *   🔴 **Pure.** The container width is an ARGUMENT, not a measurement (§6.3:911) —
 *   the hook reads no DOM, keeps no state and is tested without a stub. Its caller
 *   gets the width from `useContainerWidth` (BW4).
 *
 *   🔴 **An unmeasured width is the PATTERN DECK — never 0** (R16-B1, Nachbesserung 1;
 *   präzisiert in Nachbesserung 2 nach R16-W-N1). §4.3:623-624 settles the question
 *   once for every surface: *„eine Fläche, die ihre Breite nicht kennt, benutzt die
 *   Vorgabe ihres Musters — nie 0, nie `100 %`"*. A non-finite `containerWidth` —
 *   `null`, the value `useContainerWidth` reports before the first measurement, plus
 *   `undefined` and `NaN` — is therefore replaced by `MUSTERBREITE`
 *   (`CONTENT_WIDTH.reading`, 1280).
 *
 *   🔴 **1280 is the DECK of the `reading` pattern — not a container width, and NOT
 *   „the columns the page had before the migration"** (R16-W-N1, Nachbesserung 2).
 *   `CONTENT_WIDTH.reading` is the pattern's CAP (`tokens.js:2482`: *„das Lesemuster"* — der frühere
 *   Satz „der heutige PageContainer-Rueckfall" ist mit G0b.5 (2026-10-13) entfallen), and the concept uses it as the HEAD's cap, never as a
 *   container width (§1.2:162, §4.3:614-616, NW4.3:649). Read against the concept's
 *   own step table (§3.2.2:455-460: viewport 1280 → container 904, 1440 → 1064, 1920
 *   → 1544, 2560 → 2184), the first paint is the column set of a 1280-wide SURFACE and
 *   differs from the truth wherever the container is not 1280: a container narrower than
 *   1280 shows one or more columns TOO MANY (the fit drops them on measurement), a wider
 *   one shows too FEW and adds them. §4.3 point 2 allows exactly that (*„höchstens eine
 *   Stufe je Frame"*, NW4.6:652) — and the stepwise takeover itself belongs to the
 *   CONSUMER: the HEAD (`ViewHeader`), fed by `TableViewport` (G0b.4). This hook is pure
 *   and jumps with its argument.
 *
 *   🔴 **The pattern of the PAGE is passed IN — where it has no value, `reading`
 *   stands.** The hook does not know the page's pattern (G0b.2's `PageWidthContext`);
 *   its first consumer passes `width ?? CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading`
 *   (R16-W-N2). 🔴 The second `??` is NOT redundant: `CONTENT_WIDTH.full` is `null`
 *   (*„kein fester Deckel"*, §1.1:156), so the middle term falls THROUGH for a `full`
 *   page instead of overriding — and §4.3:614-616 names `CONTENT_WIDTH.reading` as the
 *   value for exactly that case. The fallback exists so that a caller handing over the
 *   raw `null` from `useContainerWidth` still gets a real layout instead of the
 *   narrowest one.
 *
 *   A FINITE value is taken as it is: `0` and negative numbers lie below every
 *   optional column's `max`, so the fill stops immediately and only `kern` remains.
 *   They are a caller's STATED width, not
 *   "not measured" — `useContainerWidth` never reports 0 (it discards a container
 *   that is not laid out: a closed tab, a collapsed section).
 *
 *   **The column budget is NOT returned, and that is deliberate.** The concept
 *   defines it as `Σ max(Breite) aller Spalten, die nach Priorität sichtbar sind`
 *   (§4.1:567) — a function of `visible`, not a second fact about it. Returning it
 *   here would be a second source for the same number (W12); the consumer derives
 *   it from `visible` and `COLUMN_MAX[kind]`.
 *
 *   🔴 **`hiddenCount` counts the columns a WIDER surface would show — `frei` is not
 *   among them** (R16-W1, Nachbesserung 1). A `frei` column never becomes visible on
 *   its own (§3.2.2:448, `COLUMN_PRIORITY.frei = Infinity`); it is not hidden BY
 *   WIDTH but left out BY DESIGN, and it is only reachable through the user's own
 *   column choice (§3.5:520-533). Counting it would put a constant per table into a
 *   hint that promises „n columns hidden" — and the user would open the column menu
 *   and find no column there that a wider container had shown. `hasHidden` follows
 *   the same count (`hiddenCount > 0`). Who wants „is anything at all not shown"
 *   compares `columns.length` with `visible.length` himself.
 *
 *   🔴 **Heute liest den Wert nur die Probe** (R15 W4, Nachbesserung 5, 2026-10-15):
 *   kein Produktionsverbraucher ruft `hiddenCount`/`hasHidden` ab — der `grep` ueber
 *   `ProductsTable.jsx`, `TableViewport.jsx` und `ListenMuster.jsx` bleibt leer, der
 *   einzige Leser ist `useColumnPriority.test.js`. Das ist **kein Befund**, sondern
 *   ein bewusst vorweggenommener Rueckgabewert: der Rueckweg an verdeckte Spalten
 *   ist eine SPAETERE Schicht (§3.5:537-548 stellt die Nutzerauswahl ausdruecklich
 *   hinter diese hier). Die Zeile steht hier, damit die naechste Sitzung den toten
 *   Rueckgabewert nicht fuer erledigt haelt.
 *
 *   🔴 **`visible` carries the INPUT objects UNCHANGED** (R16-W2) — same references,
 *   same order. The hidden columns are its complement
 *   (`columns.filter((c) => !visible.includes(c))`), and that works on identity
 *   alone: a later change that enriches the descriptors (e.g. `{ ...s, width: … }`,
 *   the shape BW6 needs for `columns`) would break the complement SILENTLY. Only the
 *   production error path substitutes a column (`alsKern`).
 *
 *   🔴 **Keep `columns` stable** (R15-Warnung 2): the `useMemo` depends on the array
 *   IDENTITY, so a fresh literal per render — the normal case for a JSX caller —
 *   recomputes on every render. `useMemo` in the caller or a module constant.
 *
 * @useWhen
 *   - A table must drop or keep columns by the room it has, not by the window width
 *   - The same column set must be computed for the header, the rows and the footer
 *
 * @dontUseFor
 *   - The device switch card ↔ table — that is the CALLER's decision, not this
 *     hook's. It reads the VIEWPORT breakpoint and the house precedent for wide
 *     tables is `md` (the card switch in `pages/secondLife/OpenCases.jsx` and in
 *     `/overview`'s `POOverview.jsx`, both `down('md')`); the older `sm` was the phone
 *     cut, not a rule of this hook. A caller that uses `sm` for a wide table
 *     hides the identifier column behind the scroll at tablet width
 *   - The user's own column choice (OE-W5, §3.5:520-533) — that is a later layer ON TOP of this one
 *
 * @related
 *   - useContainerWidth (BW4) — delivers the `containerWidth` this hook takes as an argument
 *   - `TableViewport` (BS14/BW6) — the surface that consumes `visible` (G0b.4)
 *
 * @reuseHints
 *   - The report tables (`/fulfillment`, `/vendors`) once they name their column steps
 *   - Any list whose footer must sit on the TABLE width — `min(Panelbreite, Spaltenbudget)` (§4.2:599)
 *
 * @param {object} args
 * @param {Array<{key: string, priority: string, kind: string, max?: number}>} args.columns
 *   The columns in DOM order. Order is preserved in `visible`, the objects are passed
 *   through unchanged. The optional `max` replaces `COLUMN_MAX[kind].max` for the BUDGET
 *   (additive; without it the type's `max` applies — byte-identical for every caller).
 * @param {number} [args.containerWidth] The container's inner width (from `useContainerWidth`);
 *   a non-finite value is the pattern DECK (`CONTENT_WIDTH.reading`), never 0.
 * @returns {{ visible: Array<object>, hiddenCount: number, hasHidden: boolean }}
 * @throws {Error} If a descriptor has no `key`, or a `priority`/`kind` outside the two closed
 *   sets — outside of `production` only. In production the fault is reported through
 *   `console.error` and the column is treated as `kern`, so that one wrong descriptor cannot
 *   tear down the page (R15-Warnung 1).
 */
export default function useColumnPriority({ columns, containerWidth }) {
  return useMemo(() => {
    const spalten = (Array.isArray(columns) ? columns : []).map((spalte, i) =>
      pruefeDescriptor(spalte, i) ? spalte : alsKern(spalte),
    )
    const breite = Number.isFinite(containerWidth) ? containerWidth : MUSTERBREITE
    const visible = waehleSichtbare(spalten, breite)
    // `frei` wird nie automatisch sichtbar und ist deshalb nicht „ausgeblendet",
    // sondern „nie von selbst da" (§3.2.2:448) — es zaehlt hier nicht mit.
    const hiddenCount = spalten.filter(
      (spalte) => spalte.priority !== FREI && !visible.includes(spalte),
    ).length
    return { visible, hiddenCount, hasHidden: hiddenCount > 0 }
  }, [columns, containerWidth])
}
